import { create } from 'zustand';
import { nanoid } from 'nanoid';
import type {
  ExportJob,
  ExportConfig,
  ExportRecord,
  ButtonUrlAssignment,
} from '../types/export';
import { DEFAULT_EXPORT_CONFIG, generatePacketFilename, getScaleSuffix, UNBRANDED_LEGACY_ID } from '../types/export';
import { runExportPipeline } from '../lib/exportEngine';
import { uploadExportFiles, saveExportRecord, fetchExportHistory } from '../lib/exportStorage';
import { fetchBrandById, fetchMappingSourceData, resolveElementsForBrand } from '../lib/brandResolver';
import { useStudioStore } from './studioStore';
import { usePreviewStore } from './previewStore';
import { useMappingStore } from './mappingStore';

interface ExportState {
  currentJob: ExportJob | null;
  config: ExportConfig;
  history: ExportRecord[];
  historyLoading: boolean;
  packetFilename: string | null;
  buttonUrls: ButtonUrlAssignment[];
  legacyId: string | null;

  setConfig: (config: Partial<ExportConfig>) => void;
  resetConfig: () => void;
  setButtonUrls: (urls: ButtonUrlAssignment[]) => void;
  setLegacyId: (id: string | null) => void;

  startExport: (brandId?: string | null) => Promise<void>;
  cancelExport: () => void;

  loadHistory: (projectId: string) => Promise<void>;
  clearHistory: () => void;

  downloadBackground: () => void;
  downloadOverlay: () => void;
  downloadComposite: () => void;
  downloadBoth: () => void;
}

export const useExportStore = create<ExportState>((set, get) => ({
  currentJob: null,
  config: { ...DEFAULT_EXPORT_CONFIG },
  history: [],
  historyLoading: false,
  packetFilename: null,
  buttonUrls: [],
  legacyId: null,

  setConfig: (partial) => {
    set((state) => ({
      config: { ...state.config, ...partial },
    }));
  },

  resetConfig: () => {
    set({ config: { ...DEFAULT_EXPORT_CONFIG } });
  },

  setButtonUrls: (urls) => {
    set({ buttonUrls: urls });
  },

  setLegacyId: (id) => {
    set({ legacyId: id });
  },

  startExport: async (brandId) => {
    const { config, buttonUrls, legacyId } = get();
    const studioStore = useStudioStore.getState();
    const previewStore = usePreviewStore.getState();

    const { activeProject, canvasState } = studioStore;
    if (!activeProject) return;

    let resolvedElements;

    if (brandId) {
      const { brand } = await fetchBrandById(brandId);
      if (brand) {
        const { elementMappings } = useMappingStore.getState();
        const sourceData = await fetchMappingSourceData(brand, canvasState.elements, elementMappings);
        resolvedElements = resolveElementsForBrand(canvasState.elements, elementMappings, brand, sourceData);
      } else {
        previewStore.resolveAllElements();
        resolvedElements = usePreviewStore.getState().resolvedElements;
      }
    } else {
      previewStore.resolveAllElements();
      resolvedElements = usePreviewStore.getState().resolvedElements;
    }

    if (!resolvedElements || resolvedElements.length === 0) {
      previewStore.resolveAllElements();
      resolvedElements = usePreviewStore.getState().resolvedElements;
    }

    const effectiveLegacyId = (legacyId && legacyId.trim()) ? legacyId.trim() : UNBRANDED_LEGACY_ID;

    const jobId = nanoid(12);
    const job: ExportJob = {
      id: jobId,
      projectId: activeProject.id,
      config: { ...config },
      progress: {
        status: 'pending',
        percent: 0,
        message: 'Initializing export...',
      },
      startedAt: new Date(),
    };

    set({ currentJob: job, packetFilename: null });

    const updateProgress = (
      percent: number,
      message: string,
      status?: ExportJob['progress']['status']
    ) => {
      set((state) => ({
        currentJob: state.currentJob
          ? {
              ...state.currentJob,
              progress: {
                ...state.currentJob.progress,
                percent,
                message,
                status: status || state.currentJob.progress.status,
              },
            }
          : null,
      }));
    };

    const EXPORT_TIMEOUT_MS = 120_000;
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Export timed out after 120 seconds')), EXPORT_TIMEOUT_MS);
    });

    try {
      updateProgress(5, 'Resolving dynamic fields...', 'resolving');

      updateProgress(10, 'Starting export pipeline...', 'rendering');

      console.log('[Export] Starting pipeline', {
        brandId: brandId || null,
        effectiveLegacyId,
        elementCount: resolvedElements.length,
        backgroundCount: resolvedElements.filter((e) => e.resolved.layer === 'BACKGROUND_LAYER').length,
        overlayCount: resolvedElements.filter((e) => e.resolved.layer === 'OVERLAY_LAYER').length,
      });

      const result = await Promise.race([
        runExportPipeline(
          resolvedElements,
          activeProject.id,
          activeProject.name,
          canvasState.dimensions.width,
          canvasState.dimensions.height,
          config,
          (percent, message) => {
            const mappedPercent = 10 + percent * 0.6;
            updateProgress(mappedPercent, message);
          },
          undefined,
          canvasState.backgroundColor,
          effectiveLegacyId,
          buttonUrls
        ),
        timeoutPromise,
      ]);

      if (!result.success || !result.outputs) {
        throw new Error(result.error || 'Export pipeline failed');
      }

      set({ packetFilename: result.packetFilename || null });

      updateProgress(75, 'Uploading to storage...', 'uploading');

      const uploadResult = await Promise.race([
        uploadExportFiles(activeProject.id, jobId, result.outputs, config),
        timeoutPromise,
      ]);

      updateProgress(90, 'Saving export record...');

      const record = await saveExportRecord({
        id: jobId,
        project_id: activeProject.id,
        template_id: null,
        export_config: config,
        background_url: uploadResult.backgroundUrl || null,
        overlay_url: uploadResult.overlayUrl || null,
        composite_url: uploadResult.compositeUrl || null,
        file_sizes: result.outputs.fileSizes,
        dimensions: result.outputs.dimensions,
        status: 'complete',
        error_message: null,
        metadata: {
          elementCount: resolvedElements.length,
          backgroundElementCount: resolvedElements.filter(
            (e) => e.resolved.layer === 'BACKGROUND_LAYER'
          ).length,
          overlayElementCount: resolvedElements.filter(
            (e) => e.resolved.layer === 'OVERLAY_LAYER'
          ).length,
          packetFilename: result.packetFilename,
        },
      });

      updateProgress(100, 'Export complete!', 'complete');

      const hasBackground = !!result.outputs.backgroundBlob;
      const hasOverlay = !!result.outputs.overlayBlob;
      const hasComposite = !!result.outputs.compositeBlob;

      set((state) => ({
        currentJob: state.currentJob
          ? {
              ...state.currentJob,
              completedAt: new Date(),
              outputs: {
                ...result.outputs!,
                backgroundUrl: uploadResult.backgroundUrl,
                overlayUrl: uploadResult.overlayUrl,
                compositeUrl: uploadResult.compositeUrl,
              },
              progress: {
                status: 'complete',
                percent: 100,
                message: 'Export complete!',
              },
            }
          : null,
        history: record ? [record, ...state.history] : state.history,
      }));

      try {
        if (hasComposite) {
          get().downloadComposite();
          if (hasOverlay) setTimeout(() => get().downloadOverlay(), 150);
        } else if (hasBackground && hasOverlay) {
          get().downloadBoth();
        } else if (hasBackground) {
          get().downloadBackground();
        } else if (hasOverlay) {
          get().downloadOverlay();
        }
      } catch (downloadErr) {
        console.warn('[Export] auto-download failed', downloadErr);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed';
      console.error('[Export] Pipeline error', err);

      set((state) => ({
        currentJob: state.currentJob
          ? {
              ...state.currentJob,
              completedAt: new Date(),
              progress: {
                status: 'failed',
                percent: state.currentJob.progress.percent,
                message: 'Export failed',
                error: message,
              },
            }
          : null,
      }));
    } finally {
      set((state) => {
        if (!state.currentJob) return state;
        const status = state.currentJob.progress.status;
        if (status === 'complete' || status === 'failed') return state;
        return {
          currentJob: {
            ...state.currentJob,
            completedAt: new Date(),
            progress: {
              status: 'failed',
              percent: state.currentJob.progress.percent,
              message: 'Export failed',
              error: 'Export ended in an unexpected state',
            },
          },
        };
      });
    }
  },

  cancelExport: () => {
    set((state) => {
      if (!state.currentJob) return { currentJob: null };
      const status = state.currentJob.progress.status;
      if (status === 'complete' || status === 'failed') {
        return { currentJob: null };
      }
      return {
        currentJob: {
          ...state.currentJob,
          completedAt: new Date(),
          progress: {
            status: 'failed',
            percent: state.currentJob.progress.percent,
            message: 'Export cancelled',
            error: 'Cancelled by user',
          },
        },
      };
    });
  },

  loadHistory: async (projectId) => {
    set({ historyLoading: true });

    try {
      const records = await fetchExportHistory(projectId);
      set({ history: records, historyLoading: false });
    } catch {
      set({ historyLoading: false });
    }
  },

  clearHistory: () => {
    set({ history: [] });
  },

  downloadBackground: () => {
    const { currentJob, packetFilename, config } = get();
    if (!currentJob?.outputs?.backgroundBlob) return;

    const baseName = packetFilename || `${currentJob.projectId}-background`;
    const scaleSuffix = getScaleSuffix(config.scale);
    const url = URL.createObjectURL(currentJob.outputs.backgroundBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}${scaleSuffix}.${config.format}`;
    a.click();
    URL.revokeObjectURL(url);
  },

  downloadOverlay: () => {
    const { currentJob, packetFilename } = get();
    if (!currentJob?.outputs?.overlayBlob) return;

    const baseName = packetFilename || `${currentJob.projectId}-overlay`;
    const url = URL.createObjectURL(currentJob.outputs.overlayBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  },

  downloadComposite: () => {
    const { currentJob, packetFilename, config } = get();
    if (!currentJob?.outputs?.compositeBlob) return;

    const baseName = packetFilename || `${currentJob.projectId}-composite`;
    const scaleSuffix = getScaleSuffix(config.scale);
    const url = URL.createObjectURL(currentJob.outputs.compositeBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}_composite${scaleSuffix}.${config.format}`;
    a.click();
    URL.revokeObjectURL(url);
  },

  downloadBoth: () => {
    get().downloadBackground();
    setTimeout(() => get().downloadOverlay(), 100);
  },
}));
